import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MessageCircle, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { PreviewBanner } from "@/components/PreviewBanner";

const Support = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [type, setType] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = () => {
    if (!type || !subject.trim() || !message.trim()) {
      toast({
        variant: "destructive",
        title: t('support_toast_error'),
      });
      return;
    }

    // This page is not wired to any backend yet (issue #201) — there is
    // nowhere for this message to go. Deliberately NOT claiming success:
    // the previous version of this handler fired a "message sent" toast
    // here while sending nothing anywhere. Do not restore that.
    toast({
      variant: "destructive",
      title: t('support_toast_not_sent_title'),
      description: t('support_toast_not_sent_desc'),
    });
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <header className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon" className="shrink-0">
            <Link to="/profile">
              <ArrowLeft className="w-6 h-6" />
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 p-3 rounded-xl text-primary">
              <MessageCircle className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t('support_title')}</h1>
              <p className="text-sm text-muted-foreground">{t('support_subtitle')}</p>
            </div>
          </div>
        </header>

        <PreviewBanner
          titleKey="support_preview_title"
          descriptionKey="support_preview_desc"
        />

        {/* Support Form */}
        <Card className="p-5">
            <div className="space-y-6">
                <div>
                    <Label htmlFor="inquiry-type">{t('support_type')}</Label>
                    <Select value={type} onValueChange={setType}>
                        <SelectTrigger id="inquiry-type" className="w-full mt-1">
                            <SelectValue placeholder={t('support_type')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="feedback">{t('support_type_feedback')}</SelectItem>
                            <SelectItem value="bug">{t('support_type_bug')}</SelectItem>
                            <SelectItem value="question">{t('support_type_question')}</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <Label htmlFor="subject">{t('support_subject')}</Label>
                    <Input
                        id="subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={t('support_subject_placeholder')}
                        className="mt-1"
                    />
                </div>

                <div>
                    <Label htmlFor="message">{t('support_message')}</Label>
                    <Textarea
                        id="message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t('support_message_placeholder')}
                        className="mt-1"
                        rows={6}
                    />
                </div>

                <Button className="w-full" onClick={handleSubmit}>
                    <Send className="w-4 h-4 mr-2" />
                    {t('support_send_button')}
                </Button>
            </div>
        </Card>
      </div>
    </div>
  );
};

export default Support;
